import { useEffect, useState } from 'react';
import * as civilAppellateService from '../../../services/civilAppellateCaseRequests';
import type { LookupItem } from '../../../mocks/criminalMasterData';

export type CivilAppellateLookupScope = 'step1' | 'step2' | 'step3' | 'step4' | 'step5' | 'review';

type LookupBundle = {
  provinces: LookupItem[];
  districts: LookupItem[];
  urgencyLevels: LookupItem[];
  deliveryMethods: LookupItem[];
  corporateInstitutes: LookupItem[];
};

const EMPTY: LookupBundle = {
  provinces: [],
  districts: [],
  urgencyLevels: [],
  deliveryMethods: [],
  corporateInstitutes: [],
};

let sharedLookups: LookupBundle = { ...EMPTY };

export function useCivilAppellateLookups(scope: CivilAppellateLookupScope, provinceId?: string) {
  const [data, setData] = useState<LookupBundle>(sharedLookups);
  const [loading, setLoading] = useState(scope !== 'step4' && scope !== 'step5');

  useEffect(() => {
    if (scope === 'step5') {
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(scope !== 'step4');

    const load = async (): Promise<Partial<LookupBundle>> => {
      if (scope === 'step4') {
        const del = await civilAppellateService.getDeliveryMethods();
        return { deliveryMethods: del.success ? del.data : [] };
      }
      if (scope === 'step1') {
        const [prov, corp] = await Promise.all([
          civilAppellateService.getProvinces(),
          civilAppellateService.getCorporateInstitutes(),
        ]);
        return {
          provinces: prov.success ? prov.data : [],
          corporateInstitutes: corp.success ? corp.data : [],
        };
      }
      if (scope === 'step2') {
        return {};
      }
      if (scope === 'step3') {
        const urg = await civilAppellateService.getUrgencyLevels();
        return { urgencyLevels: urg.success ? urg.data : [] };
      }
      const [prov, urg, del, corp] = await Promise.all([
        civilAppellateService.getProvinces(),
        civilAppellateService.getUrgencyLevels(),
        civilAppellateService.getDeliveryMethods(),
        civilAppellateService.getCorporateInstitutes(),
      ]);
      return {
        provinces: prov.success ? prov.data : [],
        urgencyLevels: urg.success ? urg.data : [],
        deliveryMethods: del.success ? del.data : [],
        corporateInstitutes: corp.success ? corp.data : [],
      };
    };

    void load().then((partial) => {
      if (!mounted) return;
      sharedLookups = { ...sharedLookups, ...partial };
      setData({ ...sharedLookups });
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [scope]);

  useEffect(() => {
    if (scope !== 'step1' && scope !== 'review') return;
    if (!provinceId) {
      setData((d) => ({ ...d, districts: [] }));
      return;
    }

    let mounted = true;
    void civilAppellateService.getDistricts(provinceId).then((r) => {
      if (!mounted) return;
      const districts = r.success ? r.data : [];
      sharedLookups = { ...sharedLookups, districts };
      setData((d) => ({ ...d, districts }));
    });
    return () => {
      mounted = false;
    };
  }, [scope, provinceId]);

  return {
    loading,
    provinces: data.provinces,
    districts: data.districts,
    urgencyLevels: data.urgencyLevels,
    deliveryMethods: data.deliveryMethods,
    corporateInstitutes: data.corporateInstitutes,
  };
}
